import { useState, useEffect, useCallback } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type UsePwaInstallResult = {
  /** Whether the install prompt is available (not already installed). */
  canInstall: boolean;
  /** Whether the app is running as an installed PWA. */
  isInstalled: boolean;
  /** Trigger the browser's native install prompt. */
  promptInstall: () => Promise<void>;
  /** Dismiss the install banner (hides for 7 days). */
  dismiss: () => void;
  /** Whether the banner was recently dismissed. */
  isDismissed: boolean;
};

const DISMISS_KEY = 'trio-pwa-install-dismissed';
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function wasDismissedRecently(): boolean {
  try {
    const ts = localStorage.getItem(DISMISS_KEY);
    if (!ts) return false;
    return Date.now() - Number(ts) < DISMISS_DURATION_MS;
  } catch {
    return false;
  }
}

export default function usePwaInstall(): UsePwaInstallResult {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isDismissed, setIsDismissed] = useState(() => wasDismissedRecently());

  useEffect(() => {
    // Check if already installed as PWA.
    const mq = window.matchMedia('(display-mode: standalone)');
    setIsInstalled(mq.matches);

    const handleDisplayChange = (e: MediaQueryListEvent): void => setIsInstalled(e.matches);
    mq.addEventListener('change', handleDisplayChange);

    // Capture the install prompt event.
    const handleBeforeInstall = (e: Event): void => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Detect when the app is installed.
    const handleAppInstalled = (): void => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      mq.removeEventListener('change', handleDisplayChange);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
    }
  }, [deferredPrompt]);

  const dismiss = useCallback(() => {
    setIsDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // noop
    }
  }, []);

  return {
    canInstall: !!deferredPrompt && !isInstalled,
    isInstalled,
    promptInstall,
    dismiss,
    isDismissed,
  };
}
